import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getErrorResponseData } from '@app/routing/Session/ErrorResponse'

import {
  ACTION_BUTTON,
  ReferralLinkUrlField,
  VARIANT,
} from '@pages/IntroducingBroker/Dashboard/ReferralLink/ReferralLinkUrlField'
import { ReferralLinkAliasesTable } from '@pages/IntroducingBroker/LinkHub/Links/components/ReferralLinkAliasesTable'
import { ReferralLinkDropdown } from '@pages/IntroducingBroker/LinkHub/Links/components/ReferralLinkDropdown'
import { SectionHeader } from '@pages/IntroducingBroker/LinkHub/Links/components/SectionHeader'
import { buildAliasShortUrl, getLinkStyles } from '@pages/IntroducingBroker/LinkHub/Links/helpers'
import { LinkProps } from '@pages/IntroducingBroker/LinkHub/Links/types'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSnackbar } from '@context/SnackbarContext'

import { Button } from '@ui/components/Button'
import { Input } from '@ui/components/Input'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { IntroducingBrokerReferralTypes } from '@model/IntroducingBrokerDetailsDto'
import { NameDto } from '@model/NameDto'

import { Box } from '@shared/components/Box'

const ALIAS_SPECIAL_CHARACTERS_VALIDATION_REGEX = /^[a-zA-Z0-9 ]+$/

export const PersonalisedTargetLink = ({ ibCode, mtCode, cardWidth }: LinkProps) => {
  const { t } = useTranslation()
  const { account } = useAccountReadContext()
  const { addSnackbar } = useSnackbar()
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const isMobile = useWindowResize()

  const domain = window.location.host

  const [ibCodeId, setIbCodeId] = useState<IntroducingBrokerReferralTypes>(
    IntroducingBrokerReferralTypes.Default
  )
  const [aliasInput, setAliasInput] = useState('')
  const [isGeneratingLoading, setIsGeneratingLoading] = useState(false)
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null)
  const [landingPageUrl, setLandingPageUrl] = useState<NameDto<string> | undefined>()

  const { data: previewLandingPages, isLoading: isLoadingPreviewLpUrlsFromCategories } = useQuery({
    queryKey: queryKeys.ib.referralMaterialsPreviewUrls(),
    queryFn: () => apiClient.getReferralMaterialsPreviewUrls(),
  })

  const introducingBrokerId = account?.clientIntroducingBroker?.id
  const hasMultipleCodes = !!ibCode && !!mtCode
  const hasSingleCode = !!ibCode || !!mtCode
  const activeReferralType = hasMultipleCodes
    ? ibCodeId
    : ibCode
    ? IntroducingBrokerReferralTypes.Default
    : IntroducingBrokerReferralTypes.MultiTier
  const mainIbSchemeId = activeReferralType === IntroducingBrokerReferralTypes.MultiTier ? 1 : 0
  const activeIbCode =
    activeReferralType === IntroducingBrokerReferralTypes.Default ? ibCode : mtCode
  const linkStyles = getLinkStyles(isMobile)

  useEffect(() => {
    setGeneratedUrl(null)
    setAliasInput('')
  }, [activeReferralType, landingPageUrl])

  const handleGenerate = async () => {
    const trimmedAlias = aliasInput.trim()

    if (!ALIAS_SPECIAL_CHARACTERS_VALIDATION_REGEX.test(trimmedAlias)) {
      const invalidChars = [...new Set(trimmedAlias.replace(/[a-zA-Z0-9 ]/g, '').split(''))].join(
        ''
      )

      addSnackbar.error({
        message: t('Validation.Special characters prohibited', { chars: `[${invalidChars}]` }),
      })

      return
    }

    if (!landingPageUrl) {
      addSnackbar.info({ message: t('Validation.Please, select a target URL first') })
      return
    }

    try {
      setIsGeneratingLoading(true)

      const referralMaterialCategory = previewLandingPages?.find((previewUrl) =>
        previewUrl.urls.some((url) => url.id === landingPageUrl.id)
      )

      const { url } = await apiClient.getReferralUrlByCategory({
        ibCode: activeIbCode as string,
        isCustomUrl: false,
        urlId: landingPageUrl.id,
        domain,
      })

      if (referralMaterialCategory) {
        await apiClient.createReferralLinkAlias({
          referralMaterialCategoryId: referralMaterialCategory.id,
          alias: trimmedAlias,
          url,
          introducingBrokerId,
          mainIbSchemeId,
          isDeleted: false,
        })

        setGeneratedUrl(buildAliasShortUrl(activeIbCode as string, trimmedAlias))
        addSnackbar.success({ message: t('Validation.Link was successfully generated') })

        queryClient.invalidateQueries({
          queryKey: queryKeys.ib.referralLinkAliases(introducingBrokerId, mainIbSchemeId),
        })
      }
    } catch (error: unknown) {
      const { response } = getErrorResponseData(error as ResponseError)
      const errorResponseCode = response?.response?.data?.code

      if (errorResponseCode === 'link_hub_alias_already_exists') {
        addSnackbar.error({
          message: t('Validation.Alias already exists', { alias: trimmedAlias }),
        })
      } else {
        addSnackbar.error({ message: t('Something went wrong! Please try again later') })
      }
    } finally {
      setIsGeneratingLoading(false)
      setAliasInput('')
    }
  }

  return (
    <Box flex direction='column' gap={!isMobile ? 'x10' : undefined}>
      <SectionHeader
        title={t('IB.LinkHubPage.Personalized target link')}
        activeTab={ibCodeId}
        setActiveTab={setIbCodeId}
        shouldRenderSegmentedControl={hasMultipleCodes}
        layout={isMobile ? 'column' : 'row'}
        isSegmentedControlWidthConstrained
      />
      <Box flex wrap gap={isMobile ? 'x10' : 'x40'}>
        <Box flex direction='column' align='flex-start' maxWidth={cardWidth} fullWidth>
          {hasSingleCode && (
            <Box
              flex
              direction='column'
              gap='x8'
              mt={isMobile ? linkStyles.mt : undefined}
              fullWidth
            >
              <ReferralLinkDropdown
                label={t('Referral Materials.Default target URLs')}
                landingPageUrl={landingPageUrl}
                setLandingPageUrl={setLandingPageUrl}
                shortenedReferralLink={undefined}
                setShortenedReferralLink={() => {}}
                landingPageUrlsFromCategories={previewLandingPages}
                isLoadingLpUrlsFromCategories={isLoadingPreviewLpUrlsFromCategories}
                resetReferralLinks={() => setGeneratedUrl(null)}
              />
            </Box>
          )}
          <Box flex direction='column' gap='x8' mt={linkStyles.mt} fullWidth>
            <Input
              label={t('IB.LinkHubPage.Add alias')}
              fullWidth
              size='lg'
              value={aliasInput}
              disabled={!landingPageUrl || isGeneratingLoading}
              onChange={(e) => setAliasInput(e.target.value)}
            />
            <Button.Secondary
              size='md'
              fullWidth
              loading={isGeneratingLoading}
              disabled={!aliasInput.trim() || !landingPageUrl}
              onClick={handleGenerate}
            >
              {t('Referral Materials.Generate')}
            </Button.Secondary>
          </Box>
          {generatedUrl && (
            <Box mt={linkStyles.mt} fullWidth>
              <ReferralLinkUrlField
                placeholder={t('IB.LinkHubPage.URL output')}
                label={t('IB.LinkHubPage.Output')}
                referralLink={generatedUrl}
                ibCode={activeIbCode}
                shortenedReferralLink={undefined}
                isShortLinkLoading={false}
                isShorteningAvailable={false}
                onShortenIBLink={async () => {}}
                isCustomUrl
                landingPageUrl={generatedUrl}
                variant={VARIANT.DEFAULT}
                buttonVariant='secondary'
                hiddenActions={[ACTION_BUTTON.SHORTEN]}
                isLinkHub
              />
            </Box>
          )}
        </Box>
        <Box
          flex
          direction='column'
          align='flex-start'
          gap='x16'
          fullWidth
          minWidth='min(808px, 100%)'
          flexItem={1}
        >
          <ReferralLinkAliasesTable
            introducingBrokerId={introducingBrokerId}
            mainIbSchemeId={mainIbSchemeId}
            ibCode={activeIbCode as string}
          />
        </Box>
      </Box>
    </Box>
  )
}
